/**
 * Oct 20 (FD-06 / FD-07) — "Picked Up — Work Incomplete": the customer takes the vehicle before the repair is
 * finished. One dialog for the FOH Workboard and the ticket kebab, on every device.
 *
 *   * a REASON is required (a preset, or the person's own words for "Other");
 *   * an EXPECTED RETURN DATE is optional and informational — not an appointment: no capacity, technician, bay
 *     or day is reserved from it;
 *   * the repair is NOT marked complete; unfinished work, notes and history stay on the ticket.
 *
 * Presentational: the caller performs the act (recordIncompletePickup / the kebab handler) through
 * mark_picked_up_incomplete_if_quiescent, which re-validates everything.
 *
 * Oct 20 addendum (L2 / L3): given the ticket, the dialog reads the server's preview and LISTS the technicians' paused
 * sessions that will end with the pickup (each technician reviews their time later; nothing is invented, no diagnostic
 * outcome is recorded) and the future days of a multi-day repair whose reservations are released. Paused work needs an
 * explicit tick; the confirmed session ids go to the server, which refuses if the set changed. Running work blocks.
 */
import { useEffect, useMemo, useState } from 'react';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import { formatInTimeZone } from 'date-fns-tz';
import {
  readIncompletePickupPreview, previewBlockerWords, type IncompletePickupPreview,
} from '@/lib/foh/pickup/departureActs';
import {
  INCOMPLETE_PICKUP_REASONS, expectedReturnDateProblem, incompletePickupReasonText, shopToday,
  type IncompletePickupReasonCode,
} from '@/lib/foh/workboard/workboardActions';

export interface IncompletePickupDialogProps {
  open: boolean;
  /** "RO #1042 · Judy Bloom’s 2003 Grand Prix" — what is leaving. */
  subject: string | null;
  shopTimezone: string;
  busy: boolean;
  /** Oct 20 addendum: the ticket leaving — when given, the dialog reads the departure preview (paused work, future days). */
  ticketId?: string | null;
  /** The third argument is the paused sessions the person confirmed ending (empty when there were none). */
  onConfirm: (reason: string, expectedReturnDate: string | null, releasePauseIds: string[]) => void;
  onCancel: () => void;
}

function when(iso: string, tz: string): string {
  const d = new Date(iso);
  return Number.isFinite(d.getTime()) ? formatInTimeZone(d, tz, 'EEE h:mm a') : iso;
}

export function IncompletePickupDialog({ open, subject, shopTimezone, busy, ticketId, onConfirm, onCancel }: IncompletePickupDialogProps) {
  const [code, setCode] = useState<IncompletePickupReasonCode | null>(null);
  const [note, setNote] = useState('');
  const [date, setDate] = useState('');
  const [preview, setPreview] = useState<IncompletePickupPreview | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [releaseConfirmed, setReleaseConfirmed] = useState(false);

  useEffect(() => {
    let live = true;
    setPreview(null); setReleaseConfirmed(false);
    if (!open || !ticketId) return () => { live = false; };
    setPreviewLoading(true);
    readIncompletePickupPreview(ticketId)
      .then((p) => { if (live) setPreview(p); })
      .finally(() => { if (live) setPreviewLoading(false); });
    return () => { live = false; };
  }, [open, ticketId]);
  const paused = preview?.pausedSessions ?? [];
  const future = preview?.futureDaysToWithdraw ?? [];
  const previewBlocker = preview && !preview.ok ? 'This vehicle cannot be recorded as picked up right now.'
    : previewBlockerWords(preview?.blocker ?? null);
  const today = useMemo(() => shopToday(shopTimezone), [shopTimezone, open]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (open) { setCode(null); setNote(''); setDate(''); }
  }, [open]);

  const reason = incompletePickupReasonText(code, note);
  const dateProblem = expectedReturnDateProblem(date, today);
  const blocker = previewBlocker
    ?? (ticketId && previewLoading ? 'Checking the technicians’ work…' : null)
    ?? (!code ? 'Choose why the vehicle is leaving.'
      : !reason ? 'Describe the reason.'
      : dateProblem
        ?? (paused.length > 0 && !releaseConfirmed ? 'Confirm ending the paused technician work listed above.' : null));

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && !busy) onCancel(); }}>
      <DialogContent className="max-h-[90vh] max-w-md overflow-y-auto" data-testid="incomplete-pickup-dialog">
        <DialogHeader>
          <DialogTitle>Picked up — work incomplete</DialogTitle>
          <DialogDescription>
            {subject ? `${subject} is leaving before the work is finished. ` : 'The vehicle is leaving before the work is finished. '}
            The repair is <strong>not</strong> marked complete; unfinished work and history stay on the ticket, and
            its space is released.
          </DialogDescription>
        </DialogHeader>

        {previewBlocker && (
          <p className="rounded-md border border-destructive/40 bg-destructive/5 p-2 text-sm" data-testid="incomplete-pickup-preview-blocker">
            {previewBlocker}
          </p>
        )}

        {paused.length > 0 && !previewBlocker && (
          <section className="space-y-2 rounded-md border border-amber-500/40 bg-amber-500/5 p-3" data-testid="incomplete-pickup-paused-work">
            <h3 className="text-sm font-semibold">Paused technician work that ends with this pickup</h3>
            <ul className="space-y-1.5 text-sm">
              {paused.map((p) => (
                <li key={p.pauseSessionId} data-testid={`incomplete-pickup-paused-${p.pauseSessionId}`}>
                  <span className="font-medium">{p.technicianName ?? 'Technician'}</span>
                  {' — paused '}{when(p.pausedAt, shopTimezone)}{' · '}{p.pauseReason}
                  {' · '}{p.measuredMinutes} min measured
                  {p.diagnosticOutcomesOwed.length > 0 && (
                    <div className="text-xs text-muted-foreground">
                      Diagnostic outcome still owed — it is not recorded by this pickup.
                    </div>
                  )}
                </li>
              ))}
            </ul>
            <p className="text-xs text-muted-foreground">
              Their measured time and pause reasons are kept exactly as recorded. Each technician reviews their own time
              later; the work stays unfinished. This is recorded as your act, not theirs.
            </p>
            <label className="flex min-h-11 items-center gap-2 text-sm">
              <input type="checkbox" className="h-5 w-5" checked={releaseConfirmed} disabled={busy}
                     onChange={(e) => setReleaseConfirmed(e.target.checked)} data-testid="incomplete-pickup-release-confirm" />
              End this paused work with the pickup
            </label>
          </section>
        )}

        {future.length > 0 && !previewBlocker && (
          <section className="space-y-1 rounded-md border p-3 text-sm" data-testid="incomplete-pickup-future-days">
            <h3 className="font-semibold">Later days of this repair are released</h3>
            <ul className="text-xs">
              {future.map((d) => (
                <li key={d.ticketId}>
                  {d.scheduledDate ?? 'Unscheduled'}{d.technicianName ? ` · ${d.technicianName}` : ''}
                  {d.predictedMinutes ? ` · ${d.predictedMinutes} min` : ''}
                </li>
              ))}
            </ul>
            <p className="text-xs text-muted-foreground">
              Their technician and bay time is freed. The days stay in the repair’s history and are never re-booked
              automatically — when the customer returns, schedule the remaining work as a continuation.
            </p>
          </section>
        )}

        <fieldset className="space-y-2">
          <legend className="text-sm font-medium">Why is the customer taking it? (required)</legend>
          <div className="grid gap-2" role="radiogroup" aria-label="Pickup reason">
            {INCOMPLETE_PICKUP_REASONS.map((r) => (
              <button
                key={r.value}
                type="button"
                role="radio"
                aria-checked={code === r.value}
                disabled={busy}
                data-testid={`incomplete-pickup-reason-${r.value}`}
                onClick={() => setCode(r.value)}
                className={cn('min-h-11 rounded-md border px-3 py-2 text-left text-sm',
                  code === r.value ? 'border-primary bg-primary/10' : 'hover:bg-muted')}
              >
                {r.label}
              </button>
            ))}
          </div>
          <Label htmlFor="incomplete-pickup-note" className="text-sm">
            {code === 'other' ? 'Reason (required)' : 'Note (optional)'}
          </Label>
          <Textarea id="incomplete-pickup-note" value={note} onChange={(e) => setNote(e.target.value)} disabled={busy}
                    rows={2} maxLength={500} data-testid="incomplete-pickup-note" />
        </fieldset>

        <div className="space-y-1">
          <Label htmlFor="incomplete-pickup-return">Expected return date (optional)</Label>
          <Input id="incomplete-pickup-return" type="date" min={today} value={date} disabled={busy}
                 onChange={(e) => setDate(e.target.value)} data-testid="incomplete-pickup-return-date" />
          <p className="text-xs text-muted-foreground">
            For the record only — this is not an appointment. No bay, technician or time is reserved.
          </p>
        </div>

        {blocker && (code || paused.length > 0) && !previewBlocker && (
          <p className="text-sm text-muted-foreground" data-testid="incomplete-pickup-blocker">{blocker}</p>
        )}

        <DialogFooter className="flex-col gap-2 sm:flex-row">
          <Button variant="outline" className="min-h-11" onClick={onCancel} disabled={busy}>Cancel</Button>
          <Button className="min-h-11" disabled={!!blocker || busy} data-testid="incomplete-pickup-confirm"
                  onClick={() => reason && onConfirm(reason, date.trim() ? date.trim() : null,
                    releaseConfirmed ? paused.map((p) => p.pauseSessionId) : [])}>
            {busy ? 'Recording…' : 'Record pickup — work incomplete'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
